import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@features/ux/components/Header";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { Badge } from "@shared/ui/badge";
import { ArrowLeft, FilterX } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@shared/ui/select";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { useNotificationDetailsForRun } from "@features/ux/history/hooks";

const getBadgeClass = (
  type: "status" | "channel" | "recipientType" | "type",
  value: string
) => {
  const baseClasses = {
    default: "bg-muted text-muted-foreground",
    // Channel types
    email: "bg-blue-50 text-blue-600",
    sms: "bg-green-50 text-green-600",
    push: "bg-purple-50 text-purple-600",
    whatsapp: "bg-green-50 text-green-600",
    // Status types
    scheduled: "bg-blue-50 text-blue-600",
    delivered: "bg-green-50 text-green-600",
    failed: "bg-red-50 text-red-600",
    pending: "bg-yellow-50 text-yellow-600",
    completed: "bg-green-50 text-green-600",
    // Recipient types
    consumer: "bg-blue-50 text-blue-600",
    technician: "bg-green-50 text-green-600",
    business: "bg-purple-50 text-purple-600",
    // Type of
    bulk: "bg-gray-900 text-white",
    individual: "bg-blue-50 text-blue-600",
  };
  return baseClasses[value?.toLowerCase()] || baseClasses.default;
};

const getInitialFilters = (params: URLSearchParams) => ({
  recipientType: params.get("recipientType") || "",
  status: params.get("status") || "",
  template: params.get("template") || "",
});

// Notification Info Card Component - Updated to work with actual API data
const NotificationInfoCard = ({
  notification,
  runDetails,
}: {
  notification: any; // From the API response
  runDetails: any; // Run details from the API
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Notification Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Type
            </label>
            <div className="mt-1">
              <Badge
                className={getBadgeClass("type", runDetails?.typeOf || "")}
              >
                <span className="mr-1">
                  {runDetails?.typeOf?.toLowerCase() === "bulk" ? "📧" : "👤"}
                </span>
                {runDetails?.typeOf?.toUpperCase() || "N/A"}
              </Badge>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Status
            </label>
            <div className="mt-1">
              <Badge
                className={getBadgeClass(
                  "status",
                  runDetails?.statusDisplay || ""
                )}
              >
                {runDetails?.statusDisplay || "N/A"}
              </Badge>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Start Date
            </label>
            <p className="mt-1 text-sm">{runDetails?.startDate || "N/A"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Start Time
            </label>
            <p className="mt-1 text-sm">{runDetails?.startedAt || "N/A"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Complete Date
            </label>
            <p className="mt-1 text-sm">{runDetails?.completeDate || "N/A"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Complete Time
            </label>
            <p className="mt-1 text-sm">{runDetails?.completedAt || "N/A"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Channel
            </label>
            <div className="mt-1">
              <Badge
                className={getBadgeClass(
                  "channel",
                  notification?.channelType || ""
                )}
              >
                {notification?.channelType || "N/A"}
              </Badge>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Template
            </label>
            <p className="mt-1 text-sm">
              {notification?.template?.templateName || "N/A"}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Success Rate
            </label>
            <p className="mt-1 text-sm font-semibold">
              {runDetails?.successRate || 0}%
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Total Recipients
            </label>
            <p className="mt-1 text-sm font-semibold">
              {runDetails?.recipientsCount || 0}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recipient Type Badge Component
const RecipientTypeBadge = ({ type }: { type: string }) => {
  return <Badge className={getBadgeClass("recipientType", type)}>{type}</Badge>;
};

const ScheduledNotificationDetails = () => {
  const { notificationId } = useParams<{ notificationId: string }>();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const [pendingFilters, setPendingFilters] = useState(
    getInitialFilters(searchParams)
  );
  const search = searchParams.get("search") || "";
  const appliedFilters = getInitialFilters(searchParams);

  // ✅ UPDATED: Use the new hook with runId (notificationId from params)
  const { data: notificationDetails, isLoading } = useNotificationDetailsForRun(
    {
      remoteUtilityId,
      runId: notificationId || "",
      search,
      page: currentPage,
    }
  );

  // ✅ UPDATED: Process the actual API response data
  const processedData = useMemo(() => {
    if (!notificationDetails?.results) return [];

    let filteredData = notificationDetails.results.map(
      (item: any, index: number) => ({
        id: item.id || index,
        recipient:
          item.recipientData?.email ||
          item.recipientData?.to ||
          (Array.isArray(item.recipientData?.to)
            ? item.recipientData.to[0]
            : "") ||
          "N/A",
        recipientType: "Consumer", // You may need to determine this from the data
        messageTemplate: item.template?.templateName || "N/A",
        runDate: item.run?.startDate || "N/A",
        runTime: item.run?.startedAt || "N/A",
        status: item.statusDisplay || "N/A",
        failureReason:
          item.metadata?.response?.Message !== "OK"
            ? item.metadata?.response?.Message || "Unknown error"
            : "-",
        channelType: item.channelType || "N/A",
      })
    );

    // Apply filters
    if (appliedFilters.recipientType) {
      filteredData = filteredData.filter(
        (item) =>
          item.recipientType.toLowerCase() ===
          appliedFilters.recipientType.toLowerCase()
      );
    }
    if (appliedFilters.status) {
      filteredData = filteredData.filter(
        (item) =>
          item.status.toLowerCase() === appliedFilters.status.toLowerCase()
      );
    }
    if (search) {
      filteredData = filteredData.filter(
        (item) =>
          item.recipient.toLowerCase().includes(search.toLowerCase()) ||
          item.messageTemplate.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filteredData;
  }, [notificationDetails, appliedFilters, search]);

  // ✅ UPDATED: Get run details from the first notification item
  const runDetails = useMemo(() => {
    return notificationDetails?.results?.[0]?.run || null;
  }, [notificationDetails]);

  // ✅ UPDATED: Get notification info from the first item
  const notificationInfo = useMemo(() => {
    return notificationDetails?.results?.[0] || null;
  }, [notificationDetails]);

  const columns: TableColumn<any>[] = [
    {
      key: "recipient",
      header: "RECIPIENT",
    },
    {
      key: "recipientType",
      header: "RECIPIENT TYPE",
      type: "custom",
      render: (recipientType) => <RecipientTypeBadge type={recipientType} />,
    },
    {
      key: "messageTemplate",
      header: "MESSAGE TEMPLATE",
    },
    {
      key: "runDate",
      header: "RUN DATE",
    },
    {
      key: "runTime",
      header: "RUN TIME",
    },
    {
      key: "status",
      header: "STATUS",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => getBadgeClass("status", value),
      },
    },
    {
      key: "failureReason",
      header: "FAILURE REASON",
      render: (value) => (
        <span
          className={value !== "-" ? "text-red-600" : "text-muted-foreground"}
        >
          {value}
        </span>
      ),
    },
  ];

  const handleApplyFilters = () => {
    const newParams: Record<string, string> = {
      ...(pendingFilters.recipientType && {
        recipientType: pendingFilters.recipientType,
      }),
      ...(pendingFilters.status && { status: pendingFilters.status }),
      ...(pendingFilters.template && { template: pendingFilters.template }),
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    setPendingFilters({ recipientType: "", status: "", template: "" });
    const newParams = Object.fromEntries(searchParams.entries());
    ["recipientType", "status", "template", "search", "page"].forEach(
      (key) => delete newParams[key]
    );
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = {
      ...Object.fromEntries(searchParams.entries()),
      search: searchTerm,
    };
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  useEffect(() => {
    setPendingFilters(getInitialFilters(searchParams));
  }, [searchParams]);

  const pagination = useMemo(() => {
    const count = notificationDetails?.count || 0;
    const itemsPerPage = 20;
    const totalPages = Math.ceil(count / itemsPerPage);

    return {
      currentPage,
      totalPages,
      totalItems: count,
      startItem: (currentPage - 1) * itemsPerPage + 1,
      endItem: Math.min(currentPage * itemsPerPage, count),
      itemsPerPage,
    };
  }, [notificationDetails, currentPage]);

  const renderSelectFilter = (
    label: string,
    field: keyof typeof pendingFilters,
    options: { key: string; value: string }[],
    placeholder = `Select ${label}`
  ) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <Select
        value={pendingFilters[field]}
        onValueChange={(value) =>
          setPendingFilters((prev) => ({ ...prev, [field]: value }))
        }
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.key} value={String(opt.key)}>
              {opt.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const filterPanel = useMemo(
    () => (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderSelectFilter("Recipient Type", "recipientType", [
            { key: "consumer", value: "Consumer" },
            { key: "technician", value: "Technician" },
            { key: "business", value: "Business" },
          ])}
          {renderSelectFilter("Status", "status", [
            { key: "delivered", value: "Delivered" },
            { key: "pending", value: "Pending" },
            { key: "failed", value: "Failed" },
            { key: "scheduled", value: "Scheduled" },
          ])}
          {renderSelectFilter("Template", "template", [
            { key: "welcome", value: "Welcome Template" },
            { key: "newsletter", value: "Newsletter Template" },
            { key: "reminder", value: "Reminder Template" },
          ])}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="default" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button variant="ghost" onClick={handleResetFilters}>
            <FilterX className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>
    ),
    [pendingFilters]
  );

  const hasActiveFilters =
    pendingFilters.recipientType ||
    pendingFilters.status ||
    pendingFilters.template;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!notificationInfo || !runDetails) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            Notification details not found
          </p>
        </div>
      </div>
    );
  }

  const displayName = `Run ID: ${runDetails.id} - ${
    notificationInfo.template?.templateName || "Unknown Template"
  }`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Header
        title="Notification Run Details"
        subtext={`Viewing details for: ${displayName}`}
      />

      <NotificationInfoCard
        notification={notificationInfo}
        runDetails={runDetails}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Recipients List</h3>
          <div className="text-sm text-muted-foreground">
            {processedData.length} total recipients
          </div>
        </div>

        <GenericTable
          data={processedData}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by recipient or template"
          emptyMessage="No recipients found"
        />
      </div>
    </div>
  );
};

export default ScheduledNotificationDetails;
